import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Spin, Modal, Tooltip, message } from 'antd'
import { UnlockOutlined } from '@ant-design/icons'
import { get } from 'lodash'
import { isAxiosError } from 'axios'
import ClientsStyle from '../../Clients/Clients'
import Button from '../../../components/Button'
import api from '../../../api'
import Layout from '../../../components/Layout'
import { ErrorModal } from '../../../components/Modal'
import { BG_COLOR } from '../../../constants/colors'
import CreateBlackListUser from '../../../components/Modal/BlackListModal/AddBlackListClientsModal'

const PAGE_SIZE = 20
const esc = (v) => v.trim().replace(/'/g, "''")

export default function BlackListClients() {
	const errorRef = useRef()
	const [clientName, setClientName] = useState('')
	const [clientPhone, setClientPhone] = useState('')
	const [clientCode, setClientCode] = useState('')
	const [filters, setFilters] = useState({ name: '', phone: '', code: '' })
	const [isLoading, setIsLoading] = useState(false)
	const [data, setData] = useState([])
	const [isAddModalOpen, setIsAddModalOpen] = useState(false)
	const [currentPage, setCurrentPage] = useState(0)

	const getBusinessPartners = useCallback(async () => {
		const conditions = ["U_BpComment eq 'Black_List'"]
		if (filters.name) conditions.push(`contains(CardName, '${esc(filters.name)}')`)
		if (filters.phone)
			conditions.push(`contains(U_Telephone, '${esc(filters.phone)}')`)
		if (filters.code) conditions.push(`contains(CardCode, '${esc(filters.code)}')`)

		setIsLoading(true)
		try {
			const res = await api.get('BusinessPartners', {
				params: {
					$select: 'CardName, CardCode, U_Telephone, U_PS, Address',
					$filter: conditions.join(' and '),
					$skip: currentPage * PAGE_SIZE,
				},
				headers: { Prefer: `odata.maxpagesize=${PAGE_SIZE}` },
			})
			setData(JSON.parse(res.data).value || [])
		} catch (err) {
			if (isAxiosError(err)) errorRef.current?.open(get(err, 'message', ''))
		} finally {
			setIsLoading(false)
		}
	}, [filters, currentPage])

	useEffect(() => {
		getBusinessPartners()
	}, [getBusinessPartners])

	const handleUnblock = (client) => {
		Modal.confirm({
			title: 'Убрать из черного списка?',
			content: `${get(client, 'CardName', '')} (${client.CardCode})`,
			okText: 'Разблокировать',
			cancelText: 'Отмена',
			onOk: async () => {
				try {
					await api.patch(`BusinessPartners('${client.CardCode}')`, {
						U_BpComment: null,
					})
					message.success('Клиент удален из черного списка')
					getBusinessPartners()
				} catch (err) {
					let msg = get(err, 'message', '')
					try {
						msg = JSON.parse(err.response.data).error.message
					} catch (e) {}
					errorRef.current?.open(msg)
				}
			},
		})
	}

	const handleSearch = () => {
		setCurrentPage(0)
		setFilters({ name: clientName, phone: clientPhone, code: clientCode })
	}

	const inputClass = 'border-[1px] border-[#DFE2E9] p-2 rounded-md w-full'

	return (
		<Layout>
			<ClientsStyle style={{ backgroundColor: BG_COLOR }}>
				<div className="container">
					<p className="productTitle">Черный список</p>
					<div className="flex flex-row gap-2 mt-6 mb-4 flex-grow">
						<input
							type="text"
							className={inputClass}
							placeholder="Имя Клиента"
							value={clientName}
							onChange={(e) => setClientName(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
						/>
						<input
							type="text"
							className={inputClass}
							placeholder="Телефон клиента"
							value={clientPhone}
							onChange={(e) => setClientPhone(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
						/>
						<input
							type="text"
							className={inputClass}
							placeholder="Код клиента"
							value={clientCode}
							onChange={(e) => setClientCode(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
						/>
						<Button onClick={handleSearch} isLoading={isLoading}>
							Поиск
						</Button>
						<Button
							onClick={() => setIsAddModalOpen(true)}
							isLoading={isLoading}
						>
							+Добавить
						</Button>
					</div>

					<div className="relative max-h-[68vh] overflow-auto mb-8 mt-6">
						<Spin spinning={isLoading}>
							<table className="w-full text-sm text-left rtl:text-right text-gray-500">
								<thead className="sticky top-0 text-xs text-gray-700 uppercase bg-gray-50">
									<tr>
										<th scope="col" className="px-6 py-4">
											Код клиента
										</th>
										<th scope="col" className="px-6 py-4">
											Имя Клиента
										</th>
										<th scope="col" className="px-6 py-4">
											Телефон
										</th>
										<th scope="col" className="px-6 py-4">
											Паспорт
										</th>
										<th scope="col" className="px-6 py-4">
											Адрес
										</th>
										<th scope="col" className="px-6 py-4" />
									</tr>
								</thead>
								<tbody>
									{data.map((v) => (
										<tr
											key={v.CardCode}
											className="bg-white border-b hover:bg-gray-50"
										>
											<td className="px-6 py-4 font-medium text-gray-900">
												{get(v, 'CardCode', '')}
											</td>
											<td className="px-6 py-4">{get(v, 'CardName', '')}</td>
											<td className="px-6 py-4">
												{get(v, 'U_Telephone', '')}
											</td>
											<td className="px-6 py-4">{get(v, 'U_PS', '')}</td>
											<td className="px-6 py-4">{get(v, 'Address', '')}</td>
											<td className="px-6 py-4 text-right">
												<Tooltip title="Разблокировать">
													<button
														type="button"
														aria-label="Разблокировать"
														className="w-9 h-9 inline-flex items-center justify-center rounded-full text-lg text-white bg-green-500 hover:bg-green-600 transition-colors"
														onClick={() => handleUnblock(v)}
													>
														<UnlockOutlined />
													</button>
												</Tooltip>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</Spin>
					</div>

					<div className="topCard">
						<div className="flex items-center gap-2">
							<Button
								disabled={currentPage === 0}
								className={'btn'}
								onClick={() => setCurrentPage((p) => p - 1)}
							>
								{'<'}
							</Button>
							<p>{currentPage + 1}</p>
							<Button
								disabled={data.length < PAGE_SIZE}
								className={'btn'}
								onClick={() => setCurrentPage((p) => p + 1)}
							>
								{'>'}
							</Button>
						</div>
					</div>
				</div>
			</ClientsStyle>
			<ErrorModal getRef={(r) => (errorRef.current = r)} />
			{isAddModalOpen && (
				<CreateBlackListUser
					isOpen={isAddModalOpen}
					onClose={() => setIsAddModalOpen(false)}
					onSuccess={getBusinessPartners}
				/>
			)}
		</Layout>
	)
}
